import { NavLink, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { authAPI } from '../../api/auth'
import DashIcon from '../ui/DashIcon'
import { useAccessRequestStats } from '../../hooks/useAccessRequests'

interface NavItem {
  id: string
  label: string
  icon: string
  count?: string
  to: string
}

const adminNav: NavItem[] = [
  { id: 'dashboard',    label: 'Dashboard',      icon: 'dashboard', to: '/admin' },
  { id: 'orders',       label: 'Tous les colis', icon: 'package',   to: '/admin/livraisons' },
  { id: 'expediteurs',  label: 'Expéditeurs',    icon: 'users',     to: '/admin/expediteurs' },
  { id: 'livreurs',     label: 'Livreurs',       icon: 'truck',     to: '/admin/livreurs' },
  { id: 'users',        label: 'Utilisateurs',   icon: 'users',     to: '/admin/utilisateurs' },
  { id: 'finances',     label: 'Finances',       icon: 'wallet',    to: '/admin/finances' },
  { id: 'echecs',         label: 'Exceptions',      icon: 'alert',     to: '/admin/echecs' },
  { id: 'access-requests', label: 'Demandes d\'accès', icon: 'users', to: '/admin/access-requests' },
  { id: 'zones',          label: 'Zones & Pricing', icon: 'pin',       to: '/admin/zones' },
  { id: 'rapports',     label: 'Analytics',      icon: 'chart',     to: '/admin/analytics' },
  { id: 'reglages',     label: 'Paramètres',     icon: 'cog',       to: '/admin/parametres' },
]

const expediteurNav: NavItem[] = [
  { id: 'dashboard',  label: 'Dashboard',  icon: 'dashboard', to: '/expediteur' },
  { id: 'colis',      label: 'Colis',      icon: 'package',   to: '/expediteur/livraisons', count: '48' },
  { id: 'pickup',     label: 'Pickup',     icon: 'truck',     to: '/expediteur/pickup', count: '3' },
  { id: 'paiements',  label: 'Paiements',  icon: 'wallet',    to: '/expediteur/paiements' },
  { id: 'clients',    label: 'Clients',    icon: 'users',     to: '/expediteur/clients' },
  { id: 'rapports',   label: 'Rapports',   icon: 'chart',     to: '/expediteur/rapports' },
]

const livreurNav: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', to: '/livreur' },
  { id: 'colis',     label: 'Mes colis', icon: 'package',   to: '/livreur/colis', count: '12' },
  { id: 'revenus',   label: 'Revenus',   icon: 'wallet',    to: '/livreur/revenus' },
  { id: 'caisse',    label: 'Caisse',    icon: 'wallet',    to: '/livreur/caisse' },
  { id: 'route',     label: 'Ma route',  icon: 'pin',       to: '/livreur/route' },
  { id: 'planning',  label: 'Planning',  icon: 'clock',     to: '/livreur/planning' },
  { id: 'profil',    label: 'Profil',    icon: 'users',     to: '/livreur/profil' },
]

const roleNavMap = { admin: adminNav, expediteur: expediteurNav, livreur: livreurNav }

const ROLE_TAG: Record<string, string> = { admin: 'OPS', expediteur: 'SHIPPER', livreur: 'COURIER' }
const ROLE_LABEL: Record<string, string> = { admin: 'Admin', expediteur: 'Expéditeur', livreur: 'Livreur' }

interface SidebarProps {
  open: boolean
}

export default function Sidebar({ open }: SidebarProps) {
  const { user, clearUser } = useAuthStore()
  const navigate = useNavigate()
  const nav = user ? (roleNavMap[user.role] ?? []) : []
  const { data: accessStats } = useAccessRequestStats(user?.role === 'admin')

  async function handleLogout() {
    try { await authAPI.logout() } catch { /* ignore */ }
    clearUser()
    navigate('/')
  }

  return (
    <aside id="app-sidebar" className={`sidebar${open ? ' open' : ''}`}>
      {/* Brand */}
      <Link to="/" className="sidebar-brand" style={{ cursor: 'pointer' }}>
        <img src="/assets/logo.png" alt="TariqGo" />
        <span className="brand-tag">{user ? ROLE_TAG[user.role] : 'CRM'}</span>
      </Link>

      {/* Role pill */}
      <div className="role-pill">
        <span className="role-pill-dot" />
        <span>{user ? ROLE_LABEL[user.role] : 'Invité'}</span>
      </div>

      <div className="sidebar-section">Menu</div>

      {nav.map(item => (
        <NavLink
          key={item.id}
          to={item.to}
          end={item.to === '/admin' || item.to === '/expediteur' || item.to === '/livreur'}
          className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
        >
          <DashIcon name={item.icon} size={18} />
          <span>{item.label}</span>
          {item.id === 'access-requests' && accessStats && accessStats.pending > 0
            ? <span className="badge-count" style={{ background: '#EF4444' }}>{accessStats.pending}</span>
            : item.count && <span className="badge-count">{item.count}</span>
          }
        </NavLink>
      ))}

      <div className="sidebar-foot">
        <b>Besoin d'aide ?</b>
        <span>Notre équipe est disponible 8h–22h heure de Tunis.</span>
        <button onClick={handleLogout}>
          <DashIcon name="logout" size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Déconnexion
        </button>
      </div>
    </aside>
  )
}
